// Dossier → Markdown (04 §2 exports).
//
// Markdown first, and with no dependency, because it is the format that always works: it opens in
// anything, it survives being pasted into a message, and it is what the owner will actually send
// himself when he wants a dossier on his phone. The heavier engines (PDF, DOCX, XLSX) are loaded
// on demand; this one is always there.
//
// It renders the STORED document, not the database. A dossier the owner exported must be the
// dossier he read on screen — regenerating it from rows at export time would silently produce a
// different document whenever the pipeline had run in between.

import type { DossierContent } from '../useDossier.js';

const CATEGORY_LABELS: Record<string, string> = {
  legal: 'Правни',
  capabilities: 'Възможности',
  freshness: 'Актуалност',
  performance: 'Скорост и техническо',
  visibility: 'Видимост и доверие',
};

const STATE_LABELS: Record<string, string> = {
  unknown: 'непроверено',
  missing: 'липсва',
  optional_missing: 'липсва (по избор)',
  broken: 'счупено',
  subpar: 'слабо',
  enhance: 'може по-добре',
  ok: 'наред',
};

function section(title: string, lines: readonly string[]): string[] {
  if (lines.length === 0) return [];
  return ['', `## ${title}`, '', ...lines];
}

function findingLines(category: DossierContent['categories'][number]): string[] {
  const body: string[] = [];
  for (const finding of category.findings) {
    body.push(`### ${finding.titleBg} — ${STATE_LABELS[finding.state] ?? finding.state}`);
    if (finding.detailBg) body.push('', finding.detailBg);
    // D15: the weakness and what to do about it are never separated, not even in an export.
    if (finding.improvementBg) body.push('', `→ ${finding.improvementBg}`);
    body.push('');
  }
  return body;
}

function callPrepLines(prep: DossierContent['call_prep']): string[] {
  const lines = [
    prep.dead_hours ? `Мъртви часове: ${prep.dead_hours}` : '',
    ...prep.discovery_questions.map((question) => `- ${question}`),
  ];
  if (prep.what_not_to_say.length > 0) {
    lines.push('', '**Не казвайте:**', ...prep.what_not_to_say.map((line) => `- ${line}`));
  }
  return lines.filter((line) => line !== '');
}

function contactLines(owner: DossierContent['owner_block']): string[] {
  return [
    owner.name ? `${owner.name}${owner.role ? ` — ${owner.role}` : ''}` : '',
    owner.direct_phone ? `Телефон: ${owner.direct_phone}` : 'Няма потвърден пряк телефон.',
  ].filter((line) => line !== '');
}

export function dossierToMarkdown(name: string, content: DossierContent): string {
  const lines: string[] = [`# ${name}`, '', content.summary];

  if (content.priority !== null) lines.push('', `**Приоритет:** ${content.priority}`);

  lines.push(
    ...section(
      'Възможност',
      [
        `Ниво: ${content.opportunity.level}`,
        content.opportunity.reason,
        content.opportunity.categories.length > 0
          ? `Засегнати: ${content.opportunity.categories.map((c) => CATEGORY_LABELS[c] ?? c).join(', ')}`
          : '',
      ].filter((line) => line !== ''),
    ),
  );

  lines.push(...section('Контакт', contactLines(content.owner_block)));

  for (const category of content.categories) {
    lines.push(
      ...section(CATEGORY_LABELS[category.category] ?? category.category, findingLines(category)),
    );
  }

  if (content.branches.length > 0) {
    lines.push(
      ...section(
        'Обекти на същия сайт',
        content.branches.map(
          (branch) => `- ${branch.businessName}${branch.address ? ` — ${branch.address}` : ''}`,
        ),
      ),
    );
  }

  lines.push(...section('Подготовка за разговора', callPrepLines(content.call_prep)));

  // The flags go LAST and are never omitted. A dossier that dropped its own caveats on the way
  // out would be a more confident document than the one on screen.
  lines.push(
    ...section(
      'Какво не е потвърдено',
      content.data_quality_flags.length > 0
        ? content.data_quality_flags.map((flag) => `- ${flag}`)
        : ['- Няма отбелязани несигурности.'],
    ),
  );

  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n')}\n`;
}
